import React, { useEffect, useState } from "react";
import { EyeSlash, Eye, Fire, CaretDown } from "@phosphor-icons/react";
import { api } from "../lib/api";

export default function AdminIncidentsCard() {
  const [open, setOpen] = useState(false);
  const [incidents, setIncidents] = useState(null);
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || incidents) return;
    api.get("/admin/incidents", { params: { days: 14 } })
      .then((r) => setIncidents(r.data))
      .catch(() => setError("Could not load incidents."));
  }, [open, incidents]);

  const toggleHide = async (inc) => {
    setBusy(inc.id);
    try {
      const path = `/admin/incidents/${inc.id}/${inc.hidden ? "unhide" : "hide"}`;
      await api.post(path);
      setIncidents((prev) =>
        prev.map((x) => (x.id === inc.id ? { ...x, hidden: !inc.hidden } : x))
      );
    } catch {
      setError("Action failed. Try again.");
    } finally {
      setBusy(null);
    }
  };

  const list = (incidents || []).filter((i) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return `${i.name} ${i.inc_num || ""} ${i.county}`.toLowerCase().includes(s);
  });

  return (
    <div className="bg-tac-surface/40 border border-tac-border rounded-sm" data-testid="admin-incidents-card">
      <button
        type="button"
        data-testid="admin-incidents-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left"
      >
        <Fire size={14} weight="fill" className="text-tac-primary" />
        <span className="text-[12px] uppercase tracking-[0.15em] font-bold text-tac-text">
          Manage incidents (hide from public)
        </span>
        <CaretDown size={12} weight="bold" className={`text-tac-dim ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          <p className="text-[13px] text-tac-faint leading-relaxed">
            Hidden incidents disappear from the public feed and map (all counties).
            Hiding survives feed refreshes — unhide anytime.
          </p>
          {error && <div className="text-xs text-tac-danger">{error}</div>}
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, inc #, county…"
            data-testid="admin-incidents-search"
            className="w-full bg-tac-bg border border-tac-border rounded-sm px-3 py-2 text-sm text-tac-text placeholder:text-tac-faint focus:outline-none focus:border-tac-primary/60"
          />
          <div className="max-h-80 overflow-y-auto divide-y divide-tac-border/60 border border-tac-border/60 rounded-sm">
            {!incidents && !error && (
              <div className="p-4 text-[13px] text-tac-dim text-center">Loading…</div>
            )}
            {incidents && list.length === 0 && (
              <div className="p-4 text-[13px] text-tac-dim text-center">No incidents match.</div>
            )}
            {list.map((inc) => (
              <div
                key={inc.id}
                className={`px-3 py-2 flex items-center justify-between gap-3 ${inc.hidden ? "opacity-60" : ""}`}
                data-testid={`admin-incident-${inc.id}`}
              >
                <div className="min-w-0">
                  <div className="font-bold text-[13px] text-tac-text truncate flex items-center gap-1.5">
                    {inc.name}
                    {inc.hidden && (
                      <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 border border-tac-danger/55 bg-tac-danger/10 text-tac-danger rounded-sm">
                        Hidden
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-tac-dim font-mono-tac truncate">
                    {inc.inc_num ? `#${inc.inc_num} · ` : ""}{inc.county} · {inc.status_label || ""}
                    {inc.date ? ` · ${new Date(inc.date).toLocaleDateString()}` : ""}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => toggleHide(inc)}
                  disabled={busy === inc.id}
                  data-testid={`admin-incident-hide-btn-${inc.id}`}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm transition-colors disabled:opacity-50 ${
                    inc.hidden
                      ? "border-tac-primary/55 text-tac-primary hover:bg-tac-primary/10"
                      : "border-tac-danger/55 text-tac-danger hover:bg-tac-danger/10"
                  }`}
                >
                  {inc.hidden ? <Eye size={11} weight="bold" /> : <EyeSlash size={11} weight="bold" />}
                  {inc.hidden ? "Unhide" : "Hide"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
